import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { LLMSettings } from "@/types";
import type { Dispatch, SetStateAction } from "react";

interface LLMTabProps {
  llmDraft: LLMSettings;
  setLlmDraft: Dispatch<SetStateAction<LLMSettings>>;
  llmSettings: LLMSettings;
  onLlmSettingsChange: (settings: LLMSettings) => void;
  toast: any;
}

export const LLMTab = ({
  llmDraft,
  setLlmDraft,
  llmSettings,
  onLlmSettingsChange,
  toast,
}: LLMTabProps) => {
  const updateLlm = <K extends keyof LLMSettings>(key: K, value: LLMSettings[K]) =>
    setLlmDraft(prev => ({ ...prev, [key]: value }));

  const isDirty = JSON.stringify(llmDraft) !== JSON.stringify(llmSettings);
  
  // Check if user is configuring localhost in production
  const isProductionDomain = typeof window !== 'undefined' && 
    !window.location.hostname.match(/^(localhost|127\.0\.0\.1|\[::1\]|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/);
  const isLocalhost = llmDraft.baseUrl?.includes('localhost') || llmDraft.baseUrl?.includes('127.0.0.1');
  const showLocalhostWarning = isProductionDomain && llmDraft.provider === 'ollama' && isLocalhost;

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="rounded-lg sm:rounded-xl border border-border/70 bg-white/80 p-3 sm:p-4 shadow-sm dark:bg-white/5">
        <h4 className="text-xs sm:text-sm font-semibold text-foreground">LLM configuration</h4>
        <p className="text-[10px] sm:text-sm text-muted-foreground">Configure how chatMCP talks to your local or remote model.</p>
        
        {showLocalhostWarning && (
          <div className="mt-2 rounded-lg border border-amber-500/50 bg-amber-50/50 p-2 sm:p-3 dark:bg-amber-900/10">
            <p className="text-[10px] sm:text-xs font-medium text-amber-900 dark:text-amber-200">⚠️ Localhost configuration detected</p>
            <p className="text-[9px] sm:text-[11px] text-amber-800 dark:text-amber-300 mt-1">
              You're running chatMCP in production but configured Ollama with localhost. This won't work because the server can't reach your local machine. 
              <strong className="block mt-1">To use Ollama:</strong>
              • Run chatMCP locally with <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded text-[9px]">bun run dev</code>, or
              • Use a remote LLM provider (OpenAI-compatible, Cloudflare Workers AI).
              • Alternatively, deploy Ollama on a server accessible by chatMCP (i.e., ngrok, cloud VM).
            </p>
          </div>
        )}
        <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-[10px] sm:text-sm font-medium text-foreground">Provider</label>
            <Select
              value={llmDraft.provider}
              onValueChange={value => updateLlm("provider", value as LLMSettings["provider"])}
            >
              <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent align="start">
                <SelectItem value="ollama">Ollama (local)</SelectItem>
                <SelectItem value="openai-compatible">OpenAI-compatible</SelectItem>
                <SelectItem value="cloudflare-workers-ai">Cloudflare Workers AI</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] sm:text-sm font-medium text-foreground">Model</label>
            <Input
              value={llmDraft.model}
              onChange={e => updateLlm("model", e.target.value)}
              placeholder={llmDraft.provider === "cloudflare-workers-ai" ? "@cf/meta/llama-3.1-8b-instruct" : "llama3.2"}
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
          </div>
        </div>
        {llmDraft.provider !== "cloudflare-workers-ai" && (
          <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Base URL</label>
              <Input
                value={llmDraft.baseUrl ?? ""}
                onChange={e => updateLlm("baseUrl", e.target.value)}
                placeholder="http://localhost:11434"
                className="h-8 sm:h-9 text-xs sm:text-sm"
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Keep this on localhost for airgapped use.</p>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">API key (optional)</label>
              <Input
                type="password"
                value={llmDraft.apiKey ?? ""}
                onChange={e => updateLlm("apiKey", e.target.value)}
                placeholder="sk-..."
                className="h-8 sm:h-9 text-xs sm:text-sm"
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">Only used for remote OpenAI-compatible endpoints.</p>
            </div>
          </div>
        )}
        {llmDraft.provider === "cloudflare-workers-ai" && (
          <>
            <div className="space-y-1">
              <label className="text-[10px] sm:text-sm font-medium text-foreground">Gateway ID (optional)</label>
              <Input
                value={llmDraft.gatewayId ?? ""}
                onChange={e => updateLlm("gatewayId", e.target.value)}
                placeholder="my-gateway"
                className="h-8 sm:h-9 text-xs sm:text-sm"
              />
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Optional AI Gateway name for caching and analytics. Create one at{" "}
                <a 
                  href="https://dash.cloudflare.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Cloudflare → AI → AI Gateway
                </a>
              </p>
            </div>
            <div className="rounded-md border border-primary/20 bg-primary/5 p-2 sm:p-3">
              <p className="text-[9px] sm:text-xs text-foreground">
                <strong>Model Examples:</strong> @cf/meta/llama-3.1-8b-instruct, @cf/mistral/mistral-7b-instruct-v0.1, @cf/qwen/qwen1.5-14b-chat-awq
                <br />
                <a 
                  href="https://developers.cloudflare.com/workers-ai/models/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  View all available models →
                </a>
              </p>
            </div>
          </>
        )}
        <div className="grid gap-2 sm:gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-[10px] sm:text-sm font-medium text-foreground">Temperature</label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={llmDraft.temperature ?? 0}
              onChange={e => updateLlm("temperature", Number(e.target.value))}
              className="h-8 sm:h-9 text-xs sm:text-sm"
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-2 sm:px-3 py-2">
            <Checkbox
              checked={llmDraft.allowExternal ?? false}
              onChange={event => updateLlm("allowExternal", event.target.checked)}
            />
            <div className="space-y-0.5 min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-foreground">Allow external calls</p>
              <p className="text-[9px] sm:text-xs text-muted-foreground">
                Leave off for local models. Enable only when you need cloud APIs.
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3 sm:mt-4">
          <Button variant="ghost" onClick={() => setLlmDraft(llmSettings)} disabled={!isDirty} className="h-8 sm:h-9 text-xs sm:text-sm">
            Reset
          </Button>
          <Button
            onClick={() => {
              try {
                onLlmSettingsChange(llmDraft);
                toast.success("LLM settings saved", {
                  description: `Provider: ${llmDraft.provider}, Model: ${llmDraft.model}`
                });
              } catch (error) {
                toast.error("Failed to save LLM settings", {
                  description: error instanceof Error ? error.message : "Unknown error"
                });
              }
            }}
            disabled={!isDirty}
            className="h-8 sm:h-9 text-xs sm:text-sm"
          >
            Save LLM settings
          </Button>
        </div>
      </div>
    </div>
  );
};
